/**
 * Searchable User List Component
 * Reusable user list with search functionality
 * Extracted from TeamDetailsModal to eliminate duplication
 */

import React, { useState } from 'react';
import { filterUsersBySearch } from '../../utils/userHelpers';

/**
 * Searchable User List Component
 *
 * @param {Object} props - Component props
 * @param {Array} props.users - Array of user objects
 * @param {Function} props.onSelectUser - Callback when user is selected
 * @param {string} props.actionLabel - Label for action button (default: 'Select')
 * @param {string} props.emptyMessage - Message when no users found
 * @param {string} props.emptySearchMessage - Message when search has no results
 * @param {boolean} props.loading - Whether data is loading
 * @param {string} props.loadingMessage - Loading message
 */
const SearchableUserList = ({
    users = [],
    onSelectUser,
    actionLabel = 'Select',
    emptyMessage = 'No users available',
    emptySearchMessage = 'No users match your search',
    loading = false,
    loadingMessage = 'Loading users...'
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredUsers = filterUsersBySearch(users, searchTerm);

    const styles = {
        searchInput: {
            width: '100%',
            padding: '0.6rem 0.75rem',
            borderRadius: '6px',
            border: '1px solid #4a6785',
            backgroundColor: 'var(--surface)',
            color: 'var(--fg-primary)',
            fontSize: '0.9rem',
            outline: 'none',
            marginBottom: '1rem',
            boxSizing: 'border-box'
        },
        userListContainer: {
            maxHeight: '400px',
            overflowY: 'auto',
            border: '1px solid #4a6785',
            borderRadius: '6px',
            backgroundColor: 'var(--surface)'
        },
        emptyState: {
            padding: '2rem',
            textAlign: 'center',
            color: '#b0bec5'
        },
        userItem: {
            padding: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #4a6785'
        },
        userInfo: {
            flex: 1
        },
        userName: {
            fontWeight: '500',
            fontSize: '0.95rem',
            color: '#f0f4f8'
        },
        userEmail: {
            fontSize: '0.85rem',
            color: '#b0bec5',
            marginTop: '0.15rem'
        },
        actionButton: {
            padding: '0.4rem 0.8rem',
            fontSize: '0.8rem',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            backgroundColor: '#27ae60',
            color: '#ffffff',
            fontWeight: '500',
            transition: 'background-color 0.2s'
        }
    };

    if (loading) {
        return (
            <div style={styles.emptyState}>
                {loadingMessage}
            </div>
        );
    }

    return (
        <>
            <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
            />

            <div style={styles.userListContainer}>
                {filteredUsers.length === 0 ? (
                    <div style={styles.emptyState}>
                        {searchTerm ? emptySearchMessage : emptyMessage}
                    </div>
                ) : (
                    filteredUsers.map((user, index) => (
                        <div
                            key={user.id}
                            style={{
                                ...styles.userItem,
                                backgroundColor: index % 2 === 0 ? 'var(--bg)' : 'var(--surface)',
                                borderBottom: index < filteredUsers.length - 1
                                    ? '1px solid #4a6785'
                                    : 'none'
                            }}
                        >
                            <div style={styles.userInfo}>
                                <div style={styles.userName}>
                                    {user.name || 'No name'}
                                </div>
                                <div style={styles.userEmail}>
                                    {user.email || 'No email'}
                                </div>
                            </div>
                            <button
                                onClick={() => onSelectUser(user)}
                                style={styles.actionButton}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#229954'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#27ae60'}
                            >
                                {actionLabel}
                            </button>
                        </div>
                    ))
                )}
            </div>
        </>
    );
};

export default SearchableUserList;

